// ============================================================================
// EXTEND INTEGRATION
// ============================================================================
// available during synchronous execution, not inside async callbacks.
const _scriptEl = document.currentScript;

// ============================================================================
// LOADERS
// ============================================================================

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = () => reject(new Error(`Extend: failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}

function loadCSS() {
    const cssHref = _scriptEl?.src?.replace('extend.js', 'extend.css');
    if (!cssHref) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssHref;
    document.head.appendChild(link);
}

async function loadExtendSDKs() {
    await loadScript('https://sdk.helloextend.com/extend-sdk-client/v1/extend-sdk-client.min.js');
    await loadScript('https://sdk.helloextend.com/extend-sdk-client-shopify-addon/v1/extend-sdk-client-shopify-addon.min.js');
}

// ============================================================================
// GLOBAL VARIABLES & CONFIGURATION
// ============================================================================

// SKILL - Replace with correct store ID
const storeId = '1a05ae56-0601-4bf1-a781-edad5b0d9d11'; // Extend Admin or Merchant Portal
const env = 'production'; // 'production' or 'dev'
const isMainCart = location.pathname.includes('/cart');
let cart = null; // populated by fetchCart(), shared across all snippets

// ============================================================================
// SHARED UTILITIES
// ============================================================================

const mkEl = (tag, cls) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    return el;
};

async function fetchCart() {
    const res = await fetch('/cart.js');
    cart = await res.json();
    return cart;
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initExtendConfiguration() {
    if (!window.Extend || !window.ExtendShopify) return;

    Extend.config({ storeId, environment: env });

    Extend.integration = {
        aftermarketModal: true,
        currency: 'USD',
        pdpOffer: true,
        pdpModalOffer: true,
        offerProperties: {},
        cartOffer: true,
        sidecartOffer: true,
        cartBalancing: true,
        atcOffer: true,
    };

    if (Shopify?.currency?.active !== Extend.integration.currency) return;

    Extend.integration.aftermarketModal && initAftermarket();
    Extend.integration.pdpOffer && initProductPageOffer();
    Extend.integration.atcOffer && initATCOffer();

    const needsCart = Extend.integration.cartOffer;
    if (needsCart) {
        fetchCart()
            .then(() => {
                Extend.integration.cartOffer && initCartOffer(cart);
            })
            .catch(err => console.error('Extend: could not fetch cart:', err));
    }
}

// ============================================================================
// BOOT
// ============================================================================

loadCSS();
loadExtendSDKs()
    .then(initExtendConfiguration)
    .catch(err => console.error('Extend: SDK failed to load:', err));

// ============================================================================
// AFTERMARKET
// ============================================================================

function initAftermarket() {
    try {
        const params = new URL(document.location).searchParams;
        const leadToken = params.get('leadToken') ?? params.get('leadtoken');
        if (!leadToken) return;

        Extend.aftermarketModal.open({
            leadToken,
            onClose(plan, product, quantity) {
                if (plan && product) {
                    ExtendShopify.addPlanToCart(
                        { plan, product, leadToken, quantity: quantity || 1 },
                        () => {
                            location.href = '/cart';
                        }
                    );
                }
            },
        });
    } catch (err) {
        console.error('EXTEND - AFTERMARKET:', err);
    }
}

// ============================================================================
// PDP OFFER
// ============================================================================

function initProductPageOffer() {
    
    const collections = window.productCollections || [];
    const primaryCollection = collections[0]?.title;

    if (meta?.page?.pageType !== 'product') return;

    // SKILL - Replace with correct form element, closest form parent to the add to cart button on a product page
    const productForm = document.querySelector('form[is="product-form"][action="/cart/add"]'); // THEME SPECIFIC
    if (!productForm) return;

    // SKILL - Replace with correct button element, sometimes not a button, this is the main add to cart
    const addToCartButton = productForm.querySelector('button[type="submit"]'); // THEME SPECIFIC
    if (!addToCartButton) return;

    const dispatchSideCart = true;
    let productCategory = meta?.product?.type ?? null;
    if (!productCategory) productCategory = primaryCollection
 
    const extendOffer = mkEl('div', 'extend-offer');
    productForm.prepend(extendOffer);

    function getVariantPrice(variantId) {
        const match = meta?.product?.variants?.find(v => v.id.toString() === variantId.toString());
        return match ? parseInt(match.price, 10) : null;
    }

    async function getOfferProps(variantId, price) {
        const { components } = (await ExtendShopify.getBundleComponents({
            variantId: `gid://shopify/ProductVariant/${variantId}`,
        })) || {};

        return components?.length > 0
            ? { referenceId: variantId, products: components }
            : { referenceId: variantId, price, category: productCategory };
    }

    async function initProductOffer() {
        try {
            if (meta?.product?.vendor === 'Extend') addToCartButton.disabled = true;

            let variantId = productForm.querySelector('[name="id"]')?.value;
            let price = getVariantPrice(variantId);
            if (!productCategory) productCategory = "Bracelet"

            Extend.integration.offerProperties = { referenceId : variantId, category: productCategory, price };
console.log('Extend', getOfferProps(variantId, price))
console.log('Extend cat', productCategory)
            Extend.buttons.render(extendOffer, Extend.integration.offerProperties);

            productForm.addEventListener('change', async function () {
                try {
                    variantId = productForm.querySelector('[name="id"]')?.value;
                    if (!variantId) return;

                    price = getVariantPrice(variantId);
                    Extend.integration.offerProperties = { variant: variantId, category: productCategory, price };
                    Extend.setActiveProduct(extendOffer, await getOfferProps(variantId, price));
                } catch (err) {
                    console.error('EXTEND - VARIANT CHANGE:', err);
                }
            });

            function handleAddToCart(e) {
                e.preventDefault();
                e.stopImmediatePropagation();

                const quantity = productForm.querySelector('[name="quantity"]')?.value || 1;

                ExtendShopify.handleAddToCart(extendOffer, {
                    quantity,
                    modal: Extend.integration.pdpModalOffer,
                    done() {
                        addToCartButton.removeEventListener('click', handleAddToCart, true);
                        addToCartButton.click();
                        addToCartButton.addEventListener('click', handleAddToCart, true);
                        if (dispatchSideCart) {
                            setTimeout(() => window.dispatchEvent(new Event('refreshAjaxSideCart')), 500);
                        }
                    },
                });
            }

            addToCartButton.addEventListener('click', handleAddToCart, true);
        } catch (err) {
            console.error('EXTEND - PRODUCT OFFER:', err);
        }
    }

    if (!Extend.buttons.instance(extendOffer)) initProductOffer();
}

// ============================================================================
// ATC OFFER — THEME SPECIFIC
// ============================================================================

function initATCOffer() {
    const atcSelector = 'button[data-quick-buy]';
    const dispatchSideCart = false;

    function runATC(e) {
        if (!e.target?.closest(atcSelector)) return;

        const el = e.target.closest(atcSelector);
        e.preventDefault();
        e.stopImmediatePropagation();
        document.removeEventListener('click', runATC, true);

        const variantId = el.getAttribute('data-variant-id');

        let productCategory;
        let productPrice;

        Extend.modal.open({
            referenceId: variantId,
            price: productPrice,
            category: productCategory,
            onClose(plan, product) {
                if (plan && product) {
                    ExtendShopify.addPlanToCart({ plan, product, quantity: 1 }, err => {
                        if (err) return;
                    });
                }
                el.click();
                document.addEventListener('click', runATC, true);
                if (dispatchSideCart) {
                    setTimeout(() => window.dispatchEvent(new Event('refreshAjaxSideCart')), 500);
                }
            },
        });
    }

    document.addEventListener('click', runATC, true);
}

// ============================================================================
// CART OFFER
// ============================================================================

function initCartOffer(initialCart) {
    if (!isMainCart && !Extend.integration.sidecartOffer) return;

    cart = initialCart;
    let init = false;

    // SKILL - Replace with container of each cart item selector
    const cartRowItem = 'line-item';
    // SKILL - Replace with cart row item image outermost element with a link selector
    const cartRowItemImage = '.line-item__media-wrapper';
    // SKILL - Replace with cart row item title selector
    const cartRowItemTitle = '.line-item__info a, .line-item__info .bold';
    // SKILL - Replace with cart row item quantity input selector
    const cartRowItemQuantity = '.quantity-input';
    // SKILL - Replace with cart row item metadata display elements selector
    const warrantyOriginalMeta = '.line-item__info p.text-sm.text-subdued'; // LOW CONFIDENCE — see summary
    // SKILL - Replace with cart row item product info container selector
    const warrantyContainer = '.line-item__info';
    // SKILL - Replace with cart row item product info container selector
    const metadataContainer = '.line-item__info';
    // SKILL - Replace with cart row item quantity wrapper selector
    const quantityWrapper = 'line-item-quantity';
    const offerClass = isMainCart ? 'extend-cart-offer' : 'extend-side-cart-offer';
    const cartEvent = isMainCart ? 'refreshAjaxCart' : 'refreshAjaxSideCart';
    const regEx = /\d+$/;

    async function refreshCart() {
        location.reload();
    }

    async function renderCartOffer(el, variantId, quantity, index) {
        try {
            if (
                ExtendShopify.warrantyAlreadyInCart(parseInt(variantId, 10), cart.items) ||
                ExtendShopify.warrantyAlreadyInCart(variantId.toString(), cart.items)
            ) {
                return;
            }

            if (isMainCart && !Extend.integration.cartOffer) return;
            if (!isMainCart && !Extend.integration.sidecartOffer) return;

            const productCategory = cart.items[index]?.product_type;
            const productPrice = cart.items[index]?.price;

            const { components } = (await ExtendShopify.getBundleComponents({
                variantId: `gid://shopify/ProductVariant/${variantId}`,
            })) || {};
            const isBundle = components?.length > 0;

            const offerProps = isBundle
                ? { referenceId: variantId, products: components }
                : { referenceId: variantId, price: productPrice, category: productCategory };

            Extend.buttons.renderSimpleOffer(el, {
                ...offerProps,
                onAddToCart(options) {
                    ExtendShopify.addPlanToCart(
                        {
                            plan: options.plan,
                            product: options.product,
                            ...(isBundle && { products: options.products }),
                            quantity,
                        },
                        function (err) {
                            if (err) {
                                console.error('EXTEND - onAddToCart:', err);
                            } else {
                                refreshCart();
                            }
                        }
                    );
                },
            });
        } catch (err) {
            console.error('EXTEND - renderCartOffer:', err);
        }
    }

    function createElement(product, index) {
        const url = product.querySelector(cartRowItemTitle)?.href;
        const variantId = url?.match(regEx)?.[0] ?? cart.items[index]?.id;
        const quantity = parseInt(product.querySelector(cartRowItemQuantity)?.value, 10) || 1;

        if (!variantId || !quantity) {
            throw new Error(`Exiting - invalid variantId (${variantId}) or quantity (${quantity})`);
        }

        const existingOffer = product.querySelector('.' + offerClass);
        if (existingOffer) {
            if (
                existingOffer.dataset.extendVariant !== variantId.toString() ||
                existingOffer.dataset.extendQuantity !== quantity.toString()
            ) {
                existingOffer.remove();
            } else {
                return;
            }
        }

        const container = product.querySelector(warrantyContainer);
        if (!container) throw new Error('Exiting - warrantyContainer unavailable');

        const newOffer = mkEl('div', offerClass);
        newOffer.setAttribute('data-extend-variant', variantId);
        newOffer.setAttribute('data-extend-quantity', quantity);
        container.append(newOffer);

        renderCartOffer(newOffer, variantId, quantity, index);
    }

    function handleStyling() {
        document.querySelectorAll(cartRowItem).forEach(function (el, index) {
            try {
                const title = el.querySelector(cartRowItemTitle);
                if (!title) throw new Error('Exiting - title unavailable');

                if (title.innerText.toLowerCase().includes('extend protection')) {
                    title.style.pointerEvents = 'none';

                    const image = el.querySelector(cartRowItemImage);
                    if (!image) throw new Error('Exiting - image unavailable');
                    image.style.pointerEvents = 'none';

                    el.querySelectorAll(warrantyOriginalMeta).forEach(each => each.remove());

                    if (quantityWrapper) {
                        el.querySelectorAll(quantityWrapper).forEach(each => {
                            each.style.opacity = '75%';
                            each.style.pointerEvents = 'none';
                        });
                    }

                    if (!cart.items[index]) return;

                    const item = cart.items[index];
                    let warrantyProductData;
                    let warrantyTermData;

                    if (item.options_with_values?.[1]) {
                        const refIdMatch = item.options_with_values[0].value.match(/\-\d{5,}/g);
                        warrantyProductData = item.options_with_values[0].value.replace(refIdMatch, '');
                        warrantyTermData = item.options_with_values[1].value;
                    } else {
                        warrantyProductData = item.properties?.Product;
                        warrantyTermData = item.properties?.Term;
                    }

                    if (warrantyProductData === 'Covered Product') {
                        const coveredId = item.properties?._Extend?.ProductId || item.properties?.['_Extend.ProductId'];
                        const coveredItem = cart.items.find(i => i.id.toString() === coveredId);
                        warrantyProductData = coveredItem?.title ?? warrantyProductData;
                    }

                    const contentContainer = el.querySelector(metadataContainer);
                    if (contentContainer && !el.querySelector('.extend-warranty-info')) {
                        const productName = mkEl('p', 'extend-warranty-info extend-warranty-info-product');
                        productName.innerHTML = `<b>Product: </b>${warrantyProductData}`;
                        productName.setAttribute('data-cy', 'warranty-description-product');

                        const productTerm = mkEl('p', 'extend-warranty-info extend-warranty-info-term');
                        productTerm.innerHTML = `<b>Term: </b>${warrantyTermData}`;
                        productTerm.setAttribute('data-cy', 'warranty-description-term');

                        contentContainer.append(productName, productTerm);
                    }
                } else {
                    createElement(el, index);
                }
            } catch (err) {
                console.error('EXTEND:', err);
            }
        });
    }

    function initEventListeners() {
        if (init) return;

        function onCartMutated() {
            fetchCart()
                .then(() => initializeCartOffer())
                .catch(err => console.error('EXTEND:', err));
        }

        // Extend's own cart-refresh trigger. Always listened for — manually
        // dispatchable (`window.dispatchEvent(new CustomEvent('extendCartDispatch'))`)
        // and also fired internally by anything else in this file that mutates the cart.
        window.addEventListener('extendCartDispatch', onCartMutated);

        // SKILL - Piggyback on the merchant's own cart-mutation event(s), if any exist.
        // Detect what custom event(s) the theme's own JS dispatches after a cart
        // mutation (add, qty change, remove, upsell add — commonly named things like
        // 'cart:updated', 'cart:refresh', 'ajaxCart:updated') and add one listener
        // per detected event name below, all calling onCartMutated.
        //
        // DETECTED: this theme dispatches `cart:change` (bubbles: true) on
        // document.documentElement after both add-to-cart (baseEvent: 'variant:add')
        // and quantity/remove updates (baseEvent: 'line-item:change') — see
        // assets/theme.js. Single event covers all cart mutations for this theme.
        window.addEventListener('cart:change', onCartMutated);
        //
        // Last resort ONLY, if no usable merchant event exists anywhere in the theme:
        // inject a single minimal `window.dispatchEvent(new CustomEvent('extendCartDispatch'))`
        // line directly into the merchant's own cart-mutation code. NOT NEEDED FOR
        // THIS MERCHANT — a usable native event was found (see above).

        // Legacy fallback trigger some earlier integrations relied on — kept for
        // backward compatibility, safe to leave even if unused.
        window.addEventListener(cartEvent, onCartMutated);

        window.addEventListener('refreshSP', refreshCart);
    }

    function initializeCartOffer() {
        if (Extend.integration.cartBalancing) {
            ExtendShopify.normalizeCart({ cart, balance: true }, function (err, data) {
                try {
                    if (err) throw new Error('normalizeCart error: ' + err);
                    if (data?.updates) refreshCart();
                } catch (err2) {
                    console.error('EXTEND:', err2);
                }
            });
        }

        handleStyling();
        initEventListeners();
        init = true;
    }

    try {
        initializeCartOffer();
    } catch (err) {
        console.error('EXTEND:', err);
    }
}
